/**
 * Projects Table Columns Configuration
 *
 * Defines table column structure, cell renderers, and sorting logic
 * for the Projects list view table layout.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
  StatusBadge,
  CategoryIcon,
  ProgressBadge,
} from "../../ui/StatusIndicators";

// Helper function to format currency
const formatCurrency = (amount) => {
  if (!amount) return "-";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Helper function to get project category
const getProjectCategory = (project) => {
  return project.type?.toLowerCase() || "residential";
};

// Helper function to format location
const formatLocation = (location) => {
  if (!location) return "-";

  // If it's already a string, return it
  if (typeof location === "string") return location;

  // If it's an object, extract address and postcode
  const parts = [];
  if (location.address) parts.push(location.address);
  if (location.postcode) parts.push(location.postcode);

  return parts.length > 0 ? parts.join(", ") : "-";
};

const ProjectsColumns = [
  {
    key: "name",
    title: "Project Name",
    width: "25%",
    sortable: true,
    render: (value, project) => (
      <div className="flex items-center space-x-3">
        <CategoryIcon category={getProjectCategory(project)} size="sm" />
        <div>
          <Link
            to={`/project/${project.id}`}
            className="font-medium text-gray-900 hover:text-indigo-600"
          >
            {value || "Untitled Project"}
          </Link>
          {project.description && (
            <p className="text-sm text-gray-500 truncate max-w-xs">
              {project.description}
            </p>
          )}
        </div>
      </div>
    ),
  },

  {
    key: "status",
    title: "Status",
    width: "15%",
    sortable: true,
    render: (value, project) => (
      <StatusBadge status={value || "planning"} type="status" />
    ),
  },

  {
    key: "type",
    title: "Type",
    width: "15%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700 capitalize">
        {value || "Residential"}
      </span>
    ),
  },

  {
    key: "location",
    title: "Location",
    width: "20%",
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-700">{formatLocation(value)}</span>
    ),
  },

  {
    key: "financial.totalCost",
    title: "Budget",
    width: "15%",
    sortable: true,
    render: (value, project) => (
      <span className="text-sm font-medium text-gray-900">
        {formatCurrency(project.financial?.totalCost)}
      </span>
    ),
  },

  {
    key: "timeline.startDate",
    title: "Start Date",
    width: "10%",
    sortable: true,
    render: (value, project) => (
      <span className="text-sm text-gray-700">
        {formatDate(project.timeline?.startDate)}
      </span>
    ),
  },
];

export default ProjectsColumns;
